import React, { useEffect, useState } from "react";
import Skeleton from "../components/Skeleton";
import { confirmAction } from "../components/ConfirmationModal";
import {
  createUser,
  deleteUser,
  listUsers,
  updateUser,
} from "../services/adminDataService";

const emptyUser = {
  name: "",
  email: "",
  phone: "",
  password: "",
  healthId: "",
  role: "pharmacist",
  isActive: true,
};

const staffRoles = [
  { value: "pharmacist", label: "Pharmacist" },
  { value: "driver", label: "Driver" },
];

const pageSize = 10;
const teoudatZehoutId = (user) => user.healthId || user.teoudatZehoutId || user.teudatZehut || user.identityNumber || "";

const getUserInitials = (user) => {
  const name = user.name || user.fullName || user.email || "User";

  return name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};

const UserFormModal = ({
  show,
  user,
  formData,
  errors,
  saving,
  onChange,
  onClose,
  onSubmit,
}) => {
  if (!show) return null;

  return (
    <>
      <div className="modal show healthid_request" style={{ display: "block" }} tabIndex="-1" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header d-flex align-items-center justify-content-between">
              <h5 className="modal-title">{user ? "Update User" : "Create User"}</h5>
              <button type="button" className="close-btns" onClick={onClose}>
                x
              </button>
            </div>
            <div className="modal-body">
              <form className="request-order-form" noValidate onSubmit={onSubmit}>
                <div className="row g-3">
                  {!user && (
                    <div className="col-lg-12">
                      <label className="form-label" htmlFor="role">Role</label>
                      <select
                        id="role"
                        name="role"
                        className={`form-select ${errors.role ? "is-invalid" : ""}`}
                        value={formData.role}
                        onChange={onChange}
                      >
                        {staffRoles.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
                      </select>
                      {errors.role && <div className="invalid-feedback d-block">{errors.role}</div>}
                    </div>
                  )}
                  {(user ? ["name", "email", "phone", "healthId"] : ["name", "email", "phone"]).map((field) => (
                    <div className="col-lg-12" key={field}>
                      <label className="form-label text-capitalize" htmlFor={field}>
                        {field === "healthId" ? "Teoudat Zehout ID" : field}
                      </label>
                      <input
                        id={field}
                        name={field}
                        type={field === "email" ? "email" : "text"}
                        className={`form-control bg-img-none ${errors[field] ? "is-invalid" : ""}`}
                        value={formData[field]}
                        onChange={onChange}
                        aria-invalid={Boolean(errors[field])}
                        aria-describedby={errors[field] ? `${field}-error` : undefined}
                      />
                      {errors[field] && (
                        <div id={`${field}-error`} className="invalid-feedback d-block">{errors[field]}</div>
                      )}
                    </div>
                  ))}

                  {!user && (
                    <div className="col-lg-12">
                      <label className="form-label" htmlFor="password">
                        Password
                      </label>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        className={`form-control bg-img-none ${errors.password ? "is-invalid" : ""}`}
                        value={formData.password}
                        onChange={onChange}
                      />
                      {errors.password && (
                        <div className="invalid-feedback d-block">{errors.password}</div>
                      )}
                    </div>
                  )}

                  <div className="col-lg-12">
                    <label className="recommendation-toggle">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={onChange}
                      />
                      <span>{user ? "Active user" : `Active ${formData.role}`}</span>
                    </label>
                  </div>

                  {errors.form && (
                    <div className="col-12">
                      <div className="alert alert-danger mb-0">{errors.form}</div>
                    </div>
                  )}

                  <div className="col-12">
                    <button type="submit" className="btn btn-primary w-100" disabled={saving}>
                      {saving ? "Saving..." : user ? "Save User" : `Create ${formData.role === "driver" ? "Driver" : "Pharmacist"}`}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose}></div>
    </>
  );
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(emptyUser);
  const [formErrors, setFormErrors] = useState({});
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(users.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visibleUsers = users.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );
  const firstVisibleUser = users.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastVisibleUser = Math.min(currentPage * pageSize, users.length);

  const loadUsers = async (filters = { search }) => {
    try {
      setLoading(true);
      setError("");
      const allUsers = [];
      const seenIds = new Set();
      const limit = 50;
      let nextPage = 1;
      while (true) {
        const results = await listUsers({ ...filters, page: nextPage, limit });
        const newUsers = results.filter((user) => user.id == null || !seenIds.has(user.id));
        newUsers.forEach((user) => { if (user.id != null) seenIds.add(user.id); });
        allUsers.push(...newUsers);
        if (newUsers.length === 0 || (results.totalPages != null ? nextPage >= results.totalPages : results.length < limit)) break;
        nextPage += 1;
      }
      setUsers(allUsers.filter((user) => String(user.role || "").trim().toLowerCase() !== "guest"));
    } catch (loadError) {
      setError(loadError.message || "Unable to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => loadUsers({ search }), search ? 250 : 0);
    return () => window.clearTimeout(timer);
    // Header search is server-backed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    const handleHeaderSearch = (event) => {
      if (event.detail?.path !== "/users") return;
      setPage(1);
      setSearch(event.detail.query || "");
    };
    window.addEventListener("admin-page-search", handleHeaderSearch);
    return () => {
      window.removeEventListener("admin-page-search", handleHeaderSearch);
    };
  }, []);

  useEffect(() => {
    const handlePageAction = (event) => {
      if (event.detail?.path !== "/users" || event.detail?.action !== "create-user") return;
      setEditingUser(null);
      setFormData(emptyUser);
      setFormErrors({});
      setError("");
      setMessage("");
      setShowModal(true);
    };
    window.addEventListener("admin-page-action", handlePageAction);
    return () => window.removeEventListener("admin-page-action", handlePageAction);
  }, []);

  useEffect(() => {
    setPage((currentValue) => Math.min(currentValue, totalPages));
  }, [totalPages]);

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      password: "",
      healthId: teoudatZehoutId(user),
      role: String(user.role || "pharmacist").toLowerCase(),
      isActive: user.isActive !== false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData(emptyUser);
    setFormErrors({});
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
    setFormErrors((previous) => ({ ...previous, [name]: "", form: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.name.trim()) nextErrors.name = "Name is required.";
    if (!formData.email.trim()) nextErrors.email = "Email is required.";
    if (!editingUser && !staffRoles.some((role) => role.value === formData.role)) nextErrors.role = "Select a valid role.";
    if (editingUser && String(editingUser.role || "user").toLowerCase() === "user" && !formData.healthId.trim()) nextErrors.healthId = "Teoudat Zehout ID is required.";
    if (!editingUser && !formData.phone.trim()) nextErrors.phone = "Phone is required.";
    if (!editingUser && !formData.password.trim()) {
      nextErrors.password = "Password is required.";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      isActive: formData.isActive,
    };

    if (editingUser) {
      if (formData.healthId.trim() || String(editingUser.role || "user").toLowerCase() === "user") payload.healthId = formData.healthId.trim();
    } else {
      payload.role = formData.role;
      payload.password = formData.password;
    }

    try {
      setSaving(true);
      if (editingUser) {
        await updateUser(editingUser.id, payload);
        setMessage("User updated successfully.");
      } else {
        await createUser(payload);
        setMessage(`${formData.role === "driver" ? "Driver" : "Pharmacist"} created successfully.`);
      }
      closeModal();
      await loadUsers();
    } catch (saveError) {
      setFormErrors({ form: saveError.message || "Unable to save user." });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!await confirmAction({ title: "Delete user?", message: `Delete ${user.name || user.email}? This cannot be undone.`, confirmLabel: "Delete", destructive: true })) return;

    try {
      await deleteUser(user.id);
      setMessage("User deleted successfully.");
      await loadUsers();
    } catch (deleteError) {
      setError(deleteError.message || "Unable to delete user.");
    }
  };

  return (
    <div className="main-wrapper users-shell table-workspace-shell">
      <div className="page-wrapper">
        <div className="page-content">
          <div className="row">
            <div className="col-12">
              <div className="card rounded-4">
                <div className="p-4">
                  {error && <div className="alert alert-danger" role="alert">{error}</div>}
                  {message && <div className="alert alert-success" role="status">{message}</div>}
                  <div className="responisve-table">
                    <table width="100%" border="0" cellSpacing="0" cellPadding="0">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Teoudat Zehout ID</th>
                          <th>Role</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading && (
                          <>
                            {[1, 2, 3].map((item) => (
                              <tr key={item}>
                                <td colSpan="6" className="py-4">
                                  <Skeleton width="100%" height="1.5rem" />
                                </td>
                              </tr>
                            ))}
                          </>
                        )}
                        {!loading && users.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center text-muted users-empty-state">
                              <strong>No users found</strong>
                              <span>Try another search or role filter.</span>
                            </td>
                          </tr>
                        )}
	                        {!loading &&
	                          visibleUsers.map((user, index) => (
                              <tr key={user.id}>
                              <td>
                                <div className="users-table-name">
                                  <span className={`users-table-avatar avatar-tone-${index % 6}`}>
                                    {getUserInitials(user)}
                                  </span>
                                  <strong>{user.name || "-"}</strong>
                                </div>
                              </td>
                              <td>{user.email || "-"}</td>
                              <td>{user.phone || "-"}</td>
                              <td>{teoudatZehoutId(user) || "-"}</td>
                              <td className="text-capitalize">{user.role || "-"}</td>
                              <td>
                                <button type="button" className="edit-icon me-2" onClick={() => openEdit(user)}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/></svg>
                                </button>
                                <button type="button" className="delete" onClick={() => handleDelete(user)}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
	                    </table>
	                  </div>
	                  {!loading && users.length > 0 && (
	                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-3">
	                      <p className="small text-muted mb-0">
	                        Showing {firstVisibleUser}-{lastVisibleUser} of {users.length}
	                      </p>
	                      <div className="d-flex align-items-center gap-2">
	                        <button
	                          type="button"
	                          className="btn btn-outline-primary pagination-button"
	                          disabled={currentPage === 1}
	                          onClick={() => setPage((value) => Math.max(1, value - 1))}
	                        >
	                          Previous
	                        </button>
	                        <span className="small text-muted">Page {currentPage} of {totalPages}</span>
	                        <button
	                          type="button"
	                          className="btn btn-outline-primary pagination-button"
	                          disabled={currentPage === totalPages}
	                          onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
	                        >
	                          Next
	                        </button>
	                      </div>
	                    </div>
	                  )}
	                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UserFormModal
        show={showModal}
        user={editingUser}
        formData={formData}
        errors={formErrors}
        saving={saving}
        onChange={handleChange}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default Users;
